import * as React from 'react'

import { Table } from '@factorialco/deprecated-design-system'
import { TimesheetPageShiftFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import { stringToDate } from 'modules/core/lib/dates'

import type Store from 'modules/attendance/components/ClockIn/Store'
import { useContractsVersion } from 'modules/attendance/lib/contractsVersionContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import type CalendarDay from 'modules/attendance/store/models/CalendarDay'

import DayColumn from './DayColumn'
import FormColumn from './FormColumn'
import OptionsColumn from './OptionsColumn'

interface RowProps {
  calendarDay: CalendarDay
  isLaborable: boolean
  date: moment.Moment
}

const Row = ({ children, isLaborable, date }: React.PropsWithChildren<RowProps>) => {
  const background = () => {
    if (!isLaborable) return 'grey100'

    if (date.isSame(moment(), 'day')) return 'viridian200'

    return 'grey000White'
  }

  return (
    <Table.Row background={background()} data-intercom-target="attendance-row">
      {children}
    </Table.Row>
  )
}

interface DayRowProps {
  date: moment.Moment
  store: Store
  calendarDay: CalendarDay
  submitHalfDayShift: (shift: TimesheetPageShiftFragment) => Promise<void>
  shifts: TimesheetPageShiftFragment[]
  canEditAttendance: boolean
}

const DayRow = ({
  date,
  store,
  calendarDay,
  submitHalfDayShift,
  shifts,
  canEditAttendance,
}: DayRowProps) => {
  const { startsOn } = useContractsVersion()
  const { attendancePeriods, reviewsStats } = useTimesheetData()

  if (!attendancePeriods?.length) return null

  const year = date.year()
  const month = date.month() + 1
  const currentPeriod = attendancePeriods?.filter((p) => p.month === month && p.year === year)
  const isReadonly = AttendanceShiftUtils.isReadOnly({
    date,
    approvedDates: reviewsStats?.approvedDates ?? [],
    startsOn,
    canEditAttendance,
    currentPeriod,
  })

  const dateTime = stringToDate(calendarDay.attributes.date, 'backend')
  const isLaborable = calendarDay.attributes.is_laborable
  const sortedShifts = shifts.sort((a, b) => (a?.halfDay || '').localeCompare(b?.halfDay || ''))

  return (
    <Row calendarDay={calendarDay} isLaborable={isLaborable} date={dateTime}>
      <DayColumn date={date} store={store} calendarDay={calendarDay} />
      <FormColumn
        store={store}
        date={date}
        disabled={isReadonly}
        submitHalfDayShift={submitHalfDayShift}
        shifts={sortedShifts}
      />
      <Table.Cell>
        <></>
      </Table.Cell>
      <OptionsColumn date={date} store={store} shifts={shifts} />
    </Row>
  )
}

export default observer(DayRow)
